'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X, ChevronRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const navItems = [
  { label: 'Home', href: '/' },
  { label: 'History', href: '/history-page' },
  { label: 'Restoration', href: '/#restoration' },
  { label: 'Specialists', href: '/specialists' },
  { label: 'Stories', href: '/community-stories' },
  { label: 'The Future', href: '/#future' },
  { label: 'Contact', href: '/#contact' },
];

export default function SiteHeader() {
  const pathname = usePathname();
  const router = useRouter();
  const { user, isAdmin, signOut, loading } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
      router?.push('/');
      router?.refresh();
    } catch {}
  };

  const isLoggedIn = !loading && !!user;

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white shadow-sm border-b border-gray-200"
      >
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="flex items-center h-16 lg:h-20">
            {/* Logo */}
            <Link href="/" className="flex items-center gap-3 no-underline flex-shrink-0">
              <AppLogo size={36} />
              <div className="flex flex-col leading-tight">
                <span className="font-serif text-base font-semibold text-foreground tracking-tight">
                  Coed Parc
                </span>
                <span className="font-sans text-xs text-muted-foreground tracking-wider uppercase">
                  Bridgend · Est. 1899
                </span>
              </div>
            </Link>

            {/* Desktop Nav — pushed to the right */}
            <nav className="hidden lg:flex items-center gap-8 ml-auto">
              {navItems?.map((item) => (
                <Link
                  key={`nav-${item?.label}`}
                  href={item?.href}
                  className={`nav-link text-sm text-foreground hover:text-primary transition-colors ${
                    pathname === item?.href ? 'active text-primary' : ''
                  }`}
                >
                  {item?.label}
                </Link>
              ))}
              {isAdmin && (
                <Link href="/admin-dashboard" className="nav-link text-sm text-accent">
                  Dashboard
                </Link>
              )}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center gap-3 ml-6">
              {isLoggedIn ? (
                <div className="flex items-center gap-3">
                  <Link href="/share-your-story" className="btn-accent text-sm px-5 py-2.5">
                    Share Your Story
                  </Link>
                  <button
                    onClick={handleSignOut}
                    className="font-sans text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Sign out
                  </button>
                </div>
              ) : (
                <Link href="/sign-up-login-screen" className="btn-primary text-sm px-5 py-2.5">
                  Sign In
                </Link>
              )}
            </div>

            {/* Mobile Hamburger */}
            <button
              className="lg:hidden p-2 text-foreground hover:text-primary transition-colors ml-auto"
              onClick={() => setMobileOpen(true)}
              aria-label="Open navigation menu"
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </header>
      {/* Mobile Drawer Overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-sm animate-fade-in"
          onClick={() => setMobileOpen(false)}
        />
      )}
      {/* Mobile Drawer */}
      <div
        className={`fixed top-0 right-0 bottom-0 z-50 w-72 bg-card shadow-2xl transform transition-transform duration-300 ease-in-out ${
          mobileOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-border">
          <span className="font-serif text-base font-semibold text-foreground">Coed Parc</span>
          <button
            onClick={() => setMobileOpen(false)}
            className="p-1.5 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Close navigation menu"
          >
            <X size={20} />
          </button>
        </div>
        <nav className="px-4 py-6 flex flex-col gap-1">
          {navItems?.map((item) => (
            <Link
              key={`mobile-nav-${item?.label}`}
              href={item?.href}
              onClick={() => setMobileOpen(false)}
              className={`flex items-center justify-between px-4 py-3 rounded text-sm font-medium transition-colors ${
                pathname === item?.href
                  ? 'bg-primary/10 text-primary' :'text-foreground hover:bg-secondary hover:text-primary'
              }`}
            >
              {item?.label}
              <ChevronRight size={14} className="text-muted-foreground" />
            </Link>
          ))}
          {isAdmin && (
            <Link
              href="/admin-dashboard"
              onClick={() => setMobileOpen(false)}
              className="flex items-center justify-between px-4 py-3 rounded text-sm font-medium text-accent hover:bg-accent/10 transition-colors"
            >
              Admin Dashboard
              <ChevronRight size={14} />
            </Link>
          )}
        </nav>
        <div className="px-6 pt-4 border-t border-border flex flex-col gap-3">
          {isLoggedIn ? (
            <>
              <Link
                href="/share-your-story"
                onClick={() => setMobileOpen(false)}
                className="btn-accent w-full text-center text-sm py-3 block"
              >
                Share Your Story
              </Link>
              <button
                onClick={() => { setMobileOpen(false); handleSignOut(); }}
                className="font-sans text-sm text-muted-foreground hover:text-foreground transition-colors text-center py-2"
              >
                Sign out
              </button>
            </>
          ) : (
            <Link
              href="/sign-up-login-screen"
              onClick={() => setMobileOpen(false)}
              className="btn-primary w-full text-center text-sm py-3 block"
            >
              Sign In
            </Link>
          )}
        </div>
      </div>
    </>
  );
}